import React from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import {
  CustomButtonWithBorder,
  CustomButton,
} from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  defaultAllSelection,
  childBoxStyle,
  nameLength,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { CustomMultiSelection } from "components/CustomComponents/CustomMultiSelection";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  ifscCodePatterns,
  atleastOneCharacterWithSpace,
} from "services/constants/patterns";

export const CreatePaymentBank = ({ onClose, reCallApi, setHanldeLoader }) => {
  const { topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const {
    setValue,
    handleSubmit,
    register,
    getValues,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      bankName: "",
      nick_name: "",
      bankAccountNumber: "",
      confirm_account_number: "",
      branchName: "",
      ifscCode: "",
      company_list: [parseInt(topbarCompanyId)],
    },
  });
  const { domain } = useSelector((state) => state.tokenReducer);
  let companyDetails = localStorage.getItem("companyAccess")
    ? JSON.parse(localStorage.getItem("companyAccess"))
    : [];

  const selectedCompanies = watch("company_list") || [];

  const company_ids = selectedCompanies.includes(defaultAllSelection)
    ? companyDetails
        .filter((c) => c.company_id !== defaultAllSelection)
        .map((c) => c.company_id)
    : selectedCompanies;

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}payroll/setup/paymentbank/payment-bank/`,
      "POST",
      {
        bank_account_number: data.bankAccountNumber,
        bank_name: data.bankName,
        bank_branch: data.branchName,
        bank_ifsc_code: data.ifscCode,
        company_ids: company_ids,
        nick_name: data.nick_name,
      }
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setValue("button_loader", false);
      onClose();
      setHanldeLoader(false);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const updateBankName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= nameLength
    ) {
      setValue("bankName", event.target.value);
    } else {
      setValue("bankName", "");
    }
  };

  const updateNickName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 64
    ) {
      setValue("nick_name", event.target.value);
    }
  };

  const updateBankAccountNumber = (event) => {
    if (
      event.target.value.length <= 18 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("bankAccountNumber", event.target.value);
    }
  };

  const updateConfirmAccountNumber = (event) => {
    if (
      event.target.value.length <= 18 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("confirm_account_number", event.target.value);
    }
  };

  const updateBranchName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 32
    ) {
      setValue("branchName", event.target.value);
    }
  };

  const updateIFSCcode = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 11
    ) {
      setValue("ifscCode", event.target.value);
    }
  };

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create Payment Bank
            </Typography>
            <Typography {...infoStyle}>
              Create your payment bank details.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Company Name</Typography>
                <Typography {...infoStyle}>
                  Select the company for which this payment bank appliess
                </Typography>
                <Controller
                  name="company_list"
                  control={control}
                  rules={{ required: "Select at least one company" }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomMultiSelection
                      value={field.value || []}
                      onChange={field.onChange}
                      options={companyDetails || []}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Bank Name</Typography>
              <Typography {...infoStyle}>
                Enter the official name of your bank
              </Typography>
              <CustomTextBox
                selectedValue={watch("bankName")}
                rules={{
                  ...register("bankName", {
                    required: "Bank name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBankName}
                placeHolder="Enter Bank Name"
                errorMessage={
                  errors?.bankName?.message ? errors?.bankName?.message : ""
                }
                textBoxError={errors.bankName ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Nick Name</Typography>
              <Typography {...infoStyle}>
                Give a short, easy-to-recognize name for this account
              </Typography>
              <CustomTextBox
                selectedValue={watch("nick_name")}
                rules={{
                  ...register("nick_name", {
                    required: "Nick name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateNickName}
                placeHolder="Enter Nick Name"
                errorMessage={
                  errors?.nick_name?.message ? errors?.nick_name?.message : ""
                }
                textBoxError={errors.nick_name ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Account Number</Typography>
              <Typography {...infoStyle}>
                Enter your full bank account number without spaces or symbols
              </Typography>
              <CustomTextBox
                selectedValue={watch("bankAccountNumber")}
                rules={{
                  ...register("bankAccountNumber", {
                    required: "Bank account number is required",
                    minLength: {
                      value: 8,
                      message: "Minimum 8 characters required",
                    },
                  }),
                }}
                updateDataFunction={updateBankAccountNumber}
                placeHolder="Enter Bank Account Number"
                errorMessage={
                  errors?.bankAccountNumber?.message
                    ? errors?.bankAccountNumber?.message
                    : ""
                }
                textBoxError={errors.bankAccountNumber ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Confirm Account Number
              </Typography>
              <Typography {...infoStyle}>
                Re-enter the account number to verify accuracy
              </Typography>
              <CustomTextBox
                selectedValue={watch("confirm_account_number")}
                rules={{
                  ...register("confirm_account_number", {
                    required: "Confirm account number is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value !== getValues("bankAccountNumber")) {
                          return "Account number and confirm account number should be same";
                        } else {
                          return true;
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateConfirmAccountNumber}
                placeHolder="Enter Confirm Account Number"
                errorMessage={
                  errors?.confirm_account_number?.message
                    ? errors?.confirm_account_number?.message
                    : ""
                }
                textBoxError={errors.confirm_account_number ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Branch</Typography>
              <Typography {...infoStyle}>
                Enter the name or location of your bank branch
              </Typography>
              <CustomTextBox
                selectedValue={watch("branchName")}
                rules={{
                  ...register("branchName", {
                    required: "Branch name is required",
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBranchName}
                placeHolder="Enter Branch Name"
                errorMessage={
                  errors?.branchName?.message ? errors?.branchName?.message : ""
                }
                textBoxError={errors.branchName ? true : false}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>IFSC Code</Typography>
              <Typography {...infoStyle}>
                Enter the IFSC code of your bank branch
              </Typography>
              <CustomTextBox
                selectedValue={watch("ifscCode")}
                rules={{
                  ...register("ifscCode", {
                    required: "IFSC code is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 11 && value !== "") {
                          return "Minimum 11 characters with no spaces";
                        } else {
                          return true;
                        }
                      },
                      checkPatterns: (value) => {
                        if (!ifscCodePatterns.test(value)) {
                          return "Please enter a valid format: ABCD0123456";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateIFSCcode}
                placeHolder="Enter IFSC Code (ex: ABCD0123456)"
                errorMessage={
                  errors?.ifscCode?.message ? errors?.ifscCode?.message : ""
                }
                textBoxError={errors.ifscCode ? true : false}
              />
            </Box>
            <Box sx={toAlignEnd}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionFuntion={handleSubmit(onSubmit)}
                actionName="Create"
                typeName="submit"
                disableAction={watch("button_loader")}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
